/** SUNTRONIX ONE — Scan-First Warehouse Home (mobile/desktop).
 *
 *  Landing screen for warehouse operators.  Five big action tiles
 *  (Inward · Rack · Pick · Pack · Dispatch) plus a global scanner
 *  input that accepts:
 *
 *    • Camera scans (see WarehouseScan)
 *    • USB HID / Bluetooth HID / keyboard-wedge scanners (via useWedgeScanner)
 *    • Manual keyboard entry
 *
 *  A scan on this screen auto-classifies the code and jumps to the
 *  right mode inside WarehouseScan.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWedgeScanner, pairBluetoothScanner } from "@/hooks/useWedgeScanner";
import {
  Boxes, MapPin, Package, PackageCheck, Truck, ScanLine, Keyboard,
  Bluetooth, Usb, Camera, ArrowRight, Loader2, AlertTriangle,
} from "lucide-react";

const TILES = [
  { key: "inward",   label: "Inward",   icon: Boxes,        color: "bg-sky-600" },
  { key: "rack",     label: "Rack",     icon: MapPin,       color: "bg-emerald-600" },
  { key: "pick",     label: "Pick",     icon: Package,      color: "bg-amber-600" },
  { key: "pack",     label: "Pack",     icon: PackageCheck, color: "bg-purple-600" },
  { key: "dispatch", label: "Dispatch", icon: Truck,        color: "bg-red-600" },
];

export default function WarehouseHome() {
  const navigate = useNavigate();
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [btName, setBtName] = useState(null);

  const classify = useCallback(async (code) => {
    if (!code || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/warehouse/classify", { code });
      setRecent((r) => [{
        at: new Date().toLocaleTimeString(),
        code, kind: data.kind, note: data.summary || "classified",
      }, ...r].slice(0, 12));

      // Jump to the right WarehouseScan mode.
      const routeMap = {
        asset:      "/mobile/warehouse-scan?mode=inward&code=",
        shipment:   "/mobile/warehouse-scan?mode=pick&code=",
        pickup:     "/mobile/warehouse-inward?pickup=",
        warehouse:  "/mobile/warehouse-scan?mode=rack&wh=",
        customer_stock: "/warehouse/scan/",
      };
      const path = routeMap[data.kind];
      if (path) navigate(`${path}${encodeURIComponent(code)}`);
      else toast.info(`Scanned "${code}" — unclassified. Choose a mode below.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Classify failed");
    } finally { setBusy(false); }
  }, [busy, navigate]);

  const { lastSource } = useWedgeScanner(classify, { enabled: true });

  const pairBt = async () => {
    try {
      const dev = await pairBluetoothScanner();
      setBtName(dev.name || "Bluetooth scanner");
      toast.success(`Paired ${dev.name || "device"}`);
    } catch (e) {
      toast.error(e.message || "Bluetooth pairing cancelled");
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 pb-16"
         data-testid="warehouse-home">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Warehouse</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-0.5">Scan to start</h1>
          <p className="text-sm text-slate-600 mt-1">
            Any scanner works — camera, USB, Bluetooth, or a keyboard-wedge.
          </p>
        </div>
        <SourceIndicator lastSource={lastSource} />
      </header>

      {/* Global scan input — accepts USB / BT HID / wedge without focus */}
      <div className="st-panel p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ScanLine className="h-5 w-5 text-slate-500" />
          <Input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && manual.trim()) {
              classify(manual.trim()); setManual("");
            } }}
            placeholder="Scan a code or type it here…"
            className="h-10 text-[14px]"
            data-testid="warehouse-home-input"
            autoFocus
          />
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                : (
            <Button onClick={() => { classify(manual.trim()); setManual(""); }}
                       disabled={!manual.trim()} data-testid="warehouse-home-go">
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Camera className="h-3 w-3" /> Camera</span>
          <span className="inline-flex items-center gap-1"><Usb className="h-3 w-3" /> USB HID</span>
          <span className="inline-flex items-center gap-1"><Keyboard className="h-3 w-3" /> Wedge</span>
          <button
            type="button"
            onClick={pairBt}
            className="inline-flex items-center gap-1 hover:text-slate-700"
            data-testid="warehouse-home-pair-bt">
            <Bluetooth className="h-3 w-3" /> {btName ? `Paired · ${btName}` : "Pair Bluetooth"}
          </button>
        </div>
      </div>

      {/* Mode tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {TILES.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              if (t.key === "inward") navigate("/mobile/warehouse-inward");
              else navigate(`/mobile/warehouse-scan?mode=${t.key}`);
            }}
            data-testid={`warehouse-tile-${t.key}`}
            className={`aspect-square rounded-xl text-white ${t.color} p-4 shadow-lg
              flex flex-col items-center justify-center gap-2 active:scale-[0.97] transition-transform`}
          >
            <t.icon className="h-8 w-8" strokeWidth={1.5} />
            <div className="text-[13px] font-semibold uppercase tracking-widest">{t.label}</div>
          </button>
        ))}
      </div>

      {/* Recent scans */}
      {recent.length > 0 && (
        <div className="st-panel">
          <div className="px-3 py-2 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Recent scans
          </div>
          <ul className="divide-y divide-slate-100">
            {recent.map((r, i) => (
              <li key={i} className="px-3 py-2 flex items-center justify-between text-[12px]">
                <div>
                  <div className="font-mono text-slate-900">{r.code}</div>
                  <div className="text-[10px] text-slate-500">{r.note}</div>
                </div>
                <div className="text-[10px] text-slate-400 tabular-nums">{r.at}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SourceIndicator({ lastSource }) {
  if (!lastSource) return null;
  const label = lastSource === "wedge"
    ? "Reading USB/BT/wedge scanner"
    : "Reading camera";
  const I = lastSource === "wedge" ? Keyboard : Camera;
  return (
    <div className="hidden md:flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
      <I className="h-3 w-3" /> {label}
    </div>
  );
}
